import { lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Reveal from '../components/Reveal';
import Footer from '../components/Footer';
import TradingViewTicker from '../components/TradingViewTicker';
import FeaturedStrategy from '../components/FeaturedStrategy';
import BacktestDisclaimer from '../components/BacktestDisclaimer';
import YggdrasilTree from '../components/YggdrasilTree';
import { hasSigil } from '../components/StrategySigil';
import PantheonDome from '../components/PantheonDome';
import { strategies as STRATEGY_REGISTRY } from '../data/registry';
import { useLanguage } from '../context/LanguageContext';
import { t, asArray } from '../utils/translations';
import { FAMILIES } from '../data/taxonomy';
import { TERMINAL_ENABLED } from '../config/features';

const SnapshotChart = lazy(() => import('../components/SnapshotChart'));

export default function Home() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const pantheon = STRATEGY_REGISTRY.filter((s) => hasSigil(s.slug));

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #060609 0%, #0a0a12 100%)' }}>
      <Navbar />

      <div className="hero-section" style={{
        minHeight: '88vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '120px 48px 60px',
      }}>
        <div style={{ maxWidth: '1100px', width: '100%', textAlign: 'center' }}>
          <Reveal>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '12px',
              color: '#00ffc8',
              letterSpacing: '3px',
              marginBottom: '20px',
              textTransform: 'uppercase',
            }}>
              {t('home.ticker.subtitle', language)}
            </div>
            <h1 className="hero-title" style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: '72px',
              color: '#fff',
              marginBottom: '20px',
              letterSpacing: '4px',
              lineHeight: 1.1,
            }}>
              {t('home.hero.title', language)}
            </h1>
            <p className="hero-subtitle" style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '20px',
              color: '#aaa',
              maxWidth: '720px',
              margin: '0 auto 36px',
              lineHeight: 1.6,
            }}>
              {t('home.hero.lead', language)}
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/strategies" style={primaryBtn}>{t('home.hero.ctaPrimary', language)}</Link>
              {TERMINAL_ENABLED && (
                <Link to="/terminal" style={secondaryBtn}>{t('home.hero.ctaTerminal', language)}</Link>
              )}
              <Link to="/reports" style={TERMINAL_ENABLED ? ghostBtn : secondaryBtn}>{t('home.hero.ctaReports', language)}</Link>
            </div>
            <p style={{
              marginTop: '22px',
              fontFamily: "'Inter', sans-serif",
              fontSize: '14px',
              color: '#777',
            }}>
              {t('home.hero.live', language)}{' · '}
              <Link to="/research" style={{ color: '#00ffc8', textDecoration: 'none' }}>{t('home.hero.livePlatforms', language)}</Link>
            </p>
            <Link to="/yggdrasil" style={{
              display: 'inline-block',
              marginTop: '14px',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '11px',
              letterSpacing: '1.5px',
              color: '#00ffc8',
              textDecoration: 'none',
              border: '1px solid rgba(0,255,200,0.3)',
              borderRadius: '999px',
              padding: '7px 14px',
            }}>
              {t('home.engineTeaser.heroLink', language)} →
            </Link>
          </Reveal>
        </div>
      </div>

      <TradingViewTicker />

      <div style={{ padding: '80px 48px 0', maxWidth: '1200px', margin: '0 auto' }}>
        <Reveal>
          <div className="engine-showcase" style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.1fr) minmax(280px, 0.9fr)',
            gap: '40px',
            alignItems: 'center',
            background: 'radial-gradient(circle at 80% 50%, rgba(0,255,200,0.07) 0%, rgba(0,0,0,0.3) 45%, rgba(0,0,0,0.3) 100%)',
            border: '1px solid rgba(0,255,200,0.18)',
            borderRadius: '18px',
            padding: '40px',
          }}>
            <div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#00ffc8', letterSpacing: '3px', marginBottom: '12px' }}>
                {t('home.engineTeaser.kicker', language)}
              </div>
              <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '52px', color: '#fff', margin: '0 0 14px', letterSpacing: '3px', lineHeight: 1 }}>
                {t('home.engineTeaser.title', language)}
              </h2>
              <p style={{ color: '#bbb', fontSize: '16px', lineHeight: 1.65, margin: '0 0 20px', maxWidth: '560px' }}>
                {t('home.engineTeaser.body', language)}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 26px', display: 'grid', gap: '10px' }}>
                {asArray(t('home.engineTeaser.points', language)).map((pt) => (
                  <li key={pt} style={{ display: 'grid', gridTemplateColumns: '14px 1fr', gap: '12px', alignItems: 'start', color: '#aaa', fontSize: '14px', lineHeight: 1.5 }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#00ffc8', marginTop: '6px', boxShadow: '0 0 10px rgba(0,255,200,0.6)' }} />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <Link to="/yggdrasil" style={primaryBtn}>{t('home.engineTeaser.cta', language)}</Link>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['56', '8+1', '3'].map((v, i) => [v, asArray(t('home.engineTeaser.stats', language))[i] || '']).map(([v, l]) => (
                    <div key={l} style={{ minWidth: '68px', textAlign: 'center', padding: '8px 10px', border: '1px solid rgba(0,255,200,0.25)', borderRadius: '10px' }}>
                      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', color: '#00ffc8', lineHeight: 1 }}>{v}</div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '8.5px', color: '#666', letterSpacing: '1px', textTransform: 'uppercase', marginTop: '3px' }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: 'block', maxWidth: '440px', margin: '0 auto', width: '100%' }}>
              <YggdrasilTree language={language} compact fruits={pantheon.map((p) => ({ slug: p.slug, name: p.name, color: p.color }))} onFruit={(slug) => navigate(`/strategies/${slug}`)} />
            </div>
          </div>
        </Reveal>
      </div>

      <div style={{ padding: '80px 48px 0', maxWidth: '1200px', margin: '0 auto' }}>
        <PantheonDome />
      </div>

      <div style={{ padding: '80px 48px 0', maxWidth: '1200px', margin: '0 auto' }}>
        <Reveal>
          <h2 style={sectionTitle}>{t('home.families.title', language)}</h2>
          <p style={sectionSub}>{t('home.families.subtitle', language)}</p>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '28px' }}>
          {FAMILIES.map((family) => (
            <Link key={family.id} to={`/strategies#${family.id}`} style={{
              textDecoration: 'none',
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '12px',
              padding: '24px',
            }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', color: '#fff', marginBottom: '8px' }}>
                {t(`taxonomy.families.${family.id}`, language)}
              </div>
              <p style={{ fontSize: '13px', color: '#888', lineHeight: 1.5 }}>
                {t(`taxonomy.familyCopy.${family.id}`, language)}
              </p>
            </Link>
          ))}
        </div>
      </div>

      <div style={{ padding: '80px 48px 0', maxWidth: '1200px', margin: '0 auto' }}>
        <Reveal>
          <div style={{
            background: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            padding: '32px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#00ffc8', letterSpacing: '2px' }}>
                  {t('home.snapshot.label', language)}
                </div>
                <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', color: '#fff', marginBottom: 0 }}>
                  {t('home.snapshot.title', language)}
                </h2>
                <p style={{ color: '#777', fontSize: '13px', marginTop: '6px', maxWidth: '520px' }}>
                  {t('home.snapshot.hint', language)}
                </p>
              </div>
              <Link to="/performance?ids=tyr,spy,qqq" className="cta-btn">{t('home.snapshot.cta', language)}</Link>
            </div>
            <Suspense fallback={<div style={{ height: 320 }} />}>
              <SnapshotChart />
            </Suspense>
          </div>
        </Reveal>
      </div>

      <div style={{ padding: '80px 48px 0', maxWidth: '1200px', margin: '0 auto' }}>
        <FeaturedStrategy />
      </div>

      {TERMINAL_ENABLED && (
        <div style={{ padding: '80px 48px 0', maxWidth: '1200px', margin: '0 auto' }}>
          <Link to="/terminal" style={{
            textDecoration: 'none',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 0,
            alignItems: 'stretch',
            background: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(0,255,200,0.28)',
            borderRadius: '16px',
            overflow: 'hidden',
          }}>
            <div style={{ padding: '40px 36px' }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#00ffc8', letterSpacing: '2px', marginBottom: '10px' }}>
                {t('home.regimeTeaser.kicker', language)}
              </div>
              <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', color: '#fff', margin: '0 0 12px', letterSpacing: '1px' }}>
                {t('home.regimeTeaser.title', language)}
              </h2>
              <p style={{ color: '#aaa', fontSize: '15px', lineHeight: 1.65, margin: '0 0 22px', maxWidth: '520px' }}>
                {t('home.regimeTeaser.body', language)}
              </p>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', color: '#00ffc8' }}>
                {t('home.regimeTeaser.cta', language)} →
              </span>
            </div>
            <div style={{ position: 'relative', minHeight: 240, overflow: 'hidden' }}>
              <img
                src="/terminal/regime.png"
                alt=""
                style={{
                  width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top',
                  display: 'block', minHeight: 240,
                  filter: 'blur(8px) saturate(1.05) brightness(1.12)',
                  transform: 'scale(1.06)',
                }}
              />
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(90deg, rgba(6,6,9,0.55) 0%, rgba(6,6,9,0.2) 50%, rgba(6,6,9,0.35) 100%)',
              }} />
              <span style={{
                position: 'absolute', top: 14, right: 14, fontSize: 13,
                background: 'rgba(6,6,14,0.72)', border: '1px solid rgba(0,255,200,0.35)',
                borderRadius: 14, padding: '3px 8px', color: '#00ffc8',
              }}>🔒</span>
            </div>
          </Link>
        </div>
      )}

      <div style={{ padding: '80px 48px 0', maxWidth: '1200px', margin: '0 auto' }}>
        <h2 style={sectionTitle}>{t('home.products.title', language)}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginTop: '24px' }}>
          <ProductCard
            kicker={t('home.products.reportsKicker', language)}
            title={t('home.products.reportsTitle', language)}
            body={t('home.products.reportsBody', language)}
            to="/reports"
            cta={t('home.products.reportsCta', language)}
            primary
          />
          {TERMINAL_ENABLED && (
            <ProductCard
              kicker={t('home.products.terminalKicker', language)}
              title={t('home.products.terminalTitle', language)}
              body={t('home.products.terminalBody', language)}
              to="/terminal"
              cta={t('home.products.terminalCta', language)}
              primary
              image="/terminal/regime.png"
              blurImage
            />
          )}
        </div>
      </div>

      <div style={{ padding: '80px 48px', maxWidth: '1200px', margin: '0 auto' }}>
        <Reveal>
          <div style={{
            background: 'linear-gradient(135deg, rgba(0,255,200,0.08), transparent)',
            border: '1px solid rgba(0,255,200,0.2)',
            borderRadius: '16px',
            padding: '48px 40px',
            textAlign: 'center',
          }}>
            <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', color: '#fff', marginBottom: '12px' }}>
              {t('home.instTeaser.title', language)}
            </h2>
            <p style={{ color: '#aaa', marginBottom: '24px' }}>{t('home.instTeaser.body', language)}</p>
            <Link to="/institutional" className="cta-btn">{t('home.instTeaser.cta', language)}</Link>
          </div>
        </Reveal>
        <BacktestDisclaimer />
      </div>

      <Footer />
    </div>
  );
}

function ProductCard({ kicker, title, body, to, cta, primary, image, blurImage }) {
  return (
    <Link to={to} style={{
      textDecoration: 'none',
      background: 'rgba(0,0,0,0.3)',
      border: primary ? '1px solid rgba(0,255,200,0.35)' : '1px solid rgba(255,255,255,0.08)',
      borderRadius: '12px',
      padding: image ? '0' : '28px',
      display: 'block',
      overflow: 'hidden',
    }}>
      {image && (
        <div style={{ position: 'relative', overflow: 'hidden' }}>
          <img
            src={image}
            alt=""
            style={{
              width: '100%', height: 148, objectFit: 'cover', objectPosition: 'top', display: 'block',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              ...(blurImage ? { filter: 'blur(8px) saturate(1.05) brightness(1.12)', transform: 'scale(1.06)' } : {}),
            }}
          />
          {blurImage && (
            <span style={{
              position: 'absolute', top: 10, right: 10, fontSize: 12,
              background: 'rgba(6,6,14,0.72)', border: '1px solid rgba(0,255,200,0.35)',
              borderRadius: 14, padding: '3px 8px', color: '#00ffc8',
            }}>🔒</span>
          )}
        </div>
      )}
      <div style={{ padding: image ? '22px 28px 28px' : 0 }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#00ffc8', marginBottom: '8px' }}>{kicker}</div>
        <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', color: '#fff', marginBottom: '10px' }}>{title}</h3>
        <p style={{ color: '#888', fontSize: '14px', lineHeight: 1.6, marginBottom: '18px' }}>{body}</p>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', color: '#00ffc8' }}>{cta} →</span>
      </div>
    </Link>
  );
}

const primaryBtn = {
  padding: '16px 40px',
  background: '#00ffc8',
  color: '#0a0a12',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  borderRadius: '6px',
  textDecoration: 'none',
};

const secondaryBtn = {
  ...primaryBtn,
  background: 'transparent',
  border: '1px solid #00ffc8',
  color: '#00ffc8',
};

const ghostBtn = {
  ...primaryBtn,
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.18)',
  color: '#ccc',
};

const sectionTitle = {
  fontFamily: "'Bebas Neue', sans-serif",
  fontSize: '40px',
  color: '#fff',
  letterSpacing: '2px',
  marginBottom: '8px',
};

const sectionSub = {
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  color: '#888',
  maxWidth: '640px',
};
